import { ReactNode } from "react";
import { Alert, Button, Group, SegmentedControl, Space, Title } from "@mantine/core";
import { IconAlertCircle, IconArchive, IconCash, IconChartBar, IconEye, IconPlus, IconSettings } from "@tabler/icons-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useCtcUser } from "./ctc_helpers";

/**
 * Commodity Trading Center shell: sub-tabs map to nested routes under /ctc.
 * Tab visibility follows the user's lanes; data access is enforced by rules + callables.
 * Design: docs/ctc-module-plan.md
 */
const CTC_TABS: Array<{ value: string; label: string; icon: ReactNode; show: (u: ReturnType<typeof useCtcUser>) => boolean }> = [
    { value: "watchlist", label: "Watch list", icon: <IconEye size={16} />, show: () => true },
    { value: "orders", label: "Archive", icon: <IconArchive size={16} />, show: () => true },
    { value: "transactions", label: "Transactions", icon: <IconCash size={16} />, show: (u) => u.has("backoffice") || u.isAdmin || u.has("dealer") || u.has("supervisor") || u.has("viewer") },
    { value: "backoffice", label: "Back office", icon: <IconSettings size={16} />, show: (u) => u.has("backoffice") || u.isAdmin },
    { value: "reports", label: "Reports", icon: <IconChartBar size={16} />, show: (u) => u.isDesk || u.has("checker") || u.isAdmin || u.has("viewer") },
];

const Ctc = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useCtcUser();

    const tabPath = (value: string) => (value === "watchlist" ? "/ctc" : `/ctc/${value}`);
    const segment = location.pathname.replace(/^\/ctc\/?/, "").split("/")[0] || "watchlist";
    const active = CTC_TABS.some((t) => t.value === segment) ? segment : "orders";
    // Makers (and admins) create orders; not shown on the new / edit order page itself
    const onOrderForm = /^\/ctc\/orders\/(new|[^/]+\/edit)\/?$/.test(location.pathname);
    const canCreate = (user.has("maker") || user.isAdmin) && !onOrderForm;

    return (
        <main style={{ padding: "1rem 0" }}>
            <Group justify="space-between">
                <Title>Commodity Trading Center</Title>
            </Group>
            <Space h="md" />
            {user.isViewOnly && (
                <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light" mb="md">
                    View-only access: you can see orders, transactions and reports, but not create or change anything.
                </Alert>
            )}
            {user.roles.length === 0 && (
                <Alert icon={<IconAlertCircle size={16} />} color="orange" mb="md">
                    Your account has no CTC role assigned. Ask an administrator to assign the appropriate role.
                </Alert>
            )}
            {/* Segmented bar: the active tab is a raised white segment in a grey bar */}
            {/* Segmented bar: the white indicator slides to the chosen tab (SegmentedControl animation) */}
            <Group justify="space-between" align="center" gap="sm">
                <SegmentedControl
                    value={active}
                    onChange={(value) => navigate(tabPath(value))}
                    radius="md" size="md" transitionDuration={250} maw="100%"
                    styles={{ root: { overflowX: "auto" }, label: { paddingInline: 14 } }}
                    data={CTC_TABS.filter((t) => t.show(user)).map((t) => ({
                        value: t.value,
                        // A click on the current tab doesn't change the value, so it's handled here: back to that tab's list
                        label: <Group gap={8} wrap="nowrap" onClick={() => { if (t.value === active) navigate(tabPath(t.value)); }}>{t.icon}<span>{t.label}</span></Group>,
                    }))}
                />
                {canCreate && (
                    <Button size="sm" leftSection={<IconPlus size={16} />} onClick={() => navigate("/ctc/orders/new")}>New purchase order</Button>
                )}
            </Group>
            <Space h="md" />
            <Outlet />
        </main>
    );
};

export default Ctc;
